import React, { useEffect, useRef, useState } from "react";
import { SVGAsset } from "res";
import UICheckbox from "../UICheckbox";
import "./styles.css";

/**
 * @function DestinationSelect
 * @param {STRING} className - Additional classes to add to the DestinationSelect
 * @param {FUNCTION} onClick - Allows us to set an event handler for the button when clicked.
 * @param {React.Component} children - React components that's being wrapped by the DestinationSelect component.
  * @param {dataPoint} dataPoint - data-point to add to wrapper for captures. 
 * @return {React.Component}
 * @exported true 
 * @example
 * const destinations = [here,there,everywhere]
 * <DestinationRow destinations={destinations}/>
 */
const DestinationSelectContext = React.createContext(null);

export default function DestinationSelect(props) {
    const [isActive, setActive] = useState(false);
    const [destinations, setDestinations] = useState({"destinations": availableDestinations()});
    const point = props["data-point"] ?? "";    
    // add an index to items for reference to select
    let destinationItems = destinations.destinations.map(function(destination,index){
        return <DestinationRow destination={destination} key={index}/>
    });
    let selectedDestinations = destinations.destinations.filter((des)=>des.selected ) 
    
    const controller = {
        //compares the value of the item and existing items from prop.children to see which one to pass to selected Item. 
        "selectItem": function(item) {
            const newValue = destinations.destinations;
            // Prevent the user from checking more than 15 items
            if(newValue.filter(destination => destination.selected === true, []).length < 15) {
                item.selected = !item.selected;
                setDestinations({ "destinations" : newValue });
            }
            
            if (props.onChange instanceof Function) {
                props.onChange({ "destinations" : newValue });
            }
        },
        "value": () => destinations,
        "toggle": () => { setActive(!isActive); },
        "close": () => setActive(false),
        "open": () => setActive(true)
    };

    const removeAll = evt => {
        const value = destinations.destinations.map(item => ({ ...item, "selected": false }), []);
        setDestinations({ "destinations" : value });
    };
   
    return (
        <div className="destination-select flex-container flex-width clickable">
            <DestinationSelectContext.Provider value={controller}>                
                <div className={`destination-select-container ${props.className ?? ""}`} data-point={point}  >
                    <DestinationSelectedItem destinations={ selectedDestinations } isActive={ isActive } onClick={ removeAll } />

                    {/*The view of the destination select when closed*/}
                    {isActive && <div className="destination-select-dropdown" > {destinationItems} </div>}
                    {isActive && <div className="destination-select-overlay" onClick={controller.close}></div>}
                </div>
            </DestinationSelectContext.Provider>
        </div>
    );
}
export const useDestinationSelect = () => React.useContext(DestinationSelectContext);
 function DestinationSelectedItem(props) {
    const controller = useDestinationSelect();

    const isEqual = () => true;
    const onClick = (evt) => {
        if (props.close !== true) {
            if (props.disabled !== true) {              
                controller.toggle();
            }
        } 

    }
   
    let selectedDestinations = props.destinations ? props.destinations.map(function(destination,index){
        return <UIDestinationTag destination={destination} key={index}/>
    }):<></>;
    return (
        <div data-point={props["data-point"] ? props["data-point"] : ""} className={`${props.disabled ? "disabled " : ""} ${isEqual(controller.value(), props.value || props.children) ? "selected " : ""}destination-select-item ${props.className ?? ""}`} onClick={onClick} >
            <div className="ui-destination-select flex-container flex-horizontal align-center-container column-space-between-container bottom-border">
                <div className="select-text flex-container flex-horizontal flex-wrap align-center-container">
                    {selectedDestinations}
                    <p>Destination</p>
                </div>
                <div className="destination-select-icons flex-container flex-horizontal align-center-container">
                    {/* X Icon shows if dropdown has destinations selected */}
                    { selectedDestinations.length > 0 && <SVGAsset id="XIcon" className="x-dropdown-icon left-inline-icon clickable" onClick={ props.onClick } /> }
                    { !props.isActive && <SVGAsset id="DownChevronIcon" className="open-dropdown-icon" /> }
                    { props.isActive && <SVGAsset id="UpChevronIcon" className="close-dropdown-icon" /> }
                </div>
            </div>
            
        </div>
    )
}

/**
 * @function DestinationRow
 * @param {STRING} className - Additional classes to add to the DestinationRow
 * @param {FUNCTION} onClick - Allows us to set an event handler for the button when clicked.
 * @param {React.Component} children - React components that's being wrapped by the DestinationRow component.
  * @param {dataPoint} dataPoint - data-point to add to wrapper for captures. 
 * @return {React.Component}
 * @exported true 
 
 */
export function DestinationRow(props) {
    const controller = useDestinationSelect();
    const point = props["data-point"] ?? "";
    const className = props.className ?? "";
    const { name, selected } = props.destination;

    const isEqual = () => true;
    const onClick = (evt) => {        
        evt.preventDefault();        
        controller.selectItem(props.destination);        
    } 
    
    return (
        <div data-point={ point } className={`${props.disabled ? "disabled " : ""} ${ isEqual(controller.value(), props.value || props.children) ? "selected" : "" } destination-select-item bottom-border ${ className }`} onClick={ onClick } >
            <UICheckbox label={ name } value={ selected } />
        </div>
    )
}

/**
* @function UIDestinationTag 
* @param {STRING} className - Additional classes to add to the UIDestinationTag 
* @param {FUNCTION} onClick - Allows us to set an event handler for the button when clicked.
* @param {React.Component} children - React components that's being wrapped by the UIDestinationTag  component.
* @param {dataPoint} dataPoint - data-point to add to wrapper for captures. 
* @return {React.Component}
* @exported true 
 
*/
function UIDestinationTag(props) {
    const className = props.className ?? "";
    const destination = props.destination;
    const dataPoint = props.dataPoint ? { "data-point": props.dataPoint } : {};

    const controller = useDestinationSelect();
    const onClick = evt => {
        evt.preventDefault();
        evt.stopPropagation();

        controller.selectItem(props.destination);
    };

    return (
        <button className={className + " ui-button set-width flex-container flex-horizontal align-center-container"} onClick={ onClick }>
            <span {...dataPoint}>{destination.name}</span>
            <SVGAsset id="XCircleIcon" className="destination-item-x-icon" />
        </button>
    );
}

export function availableDestinations(name) {
    const countries = [
        { "name": "Afghanistan", "abv": "AF", "selected":false, "isState":false},
        { "name": "Aland Islands", "abv": "AX", "selected":false, "isState":false},
        { "name": "Albania", "abv": "AL", "selected":false, "isState":false},
        { "name": "Algeria", "abv": "DZ", "selected":false, "isState":false},
        { "name": "American Samoa", "abv": "AS", "selected":false, "isState":false},
        { "name": "AndorrA", "abv": "AD", "selected":false, "isState":false},
        { "name": "Angola", "abv": "AO", "selected":false, "isState":false},
        { "name": "Anguilla", "abv": "AI", "selected":false, "isState":false},
        { "name": "Antarctica", "abv": "AQ", "selected":false, "isState":false},
        { "name": "Antigua and Barbuda", "abv": "AG", "selected":false, "isState":false},
        { "name": "Argentina", "abv": "AR", "selected":false, "isState":false},
        { "name": "Armenia", "abv": "AM", "selected":false, "isState":false},
        { "name": "Aruba", "abv": "AW", "selected":false, "isState":false},
        { "name": "Australia", "abv": "AU", "selected":false, "isState":false},
        { "name": "Austria", "abv": "AT", "selected":false, "isState":false},
        { "name": "Azerbaijan", "abv": "AZ", "selected":false, "isState":false},
        { "name": "Bahamas", "abv": "BS", "selected":false, "isState":false},
        { "name": "Bahrain", "abv": "BH", "selected":false, "isState":false},
        { "name": "Bangladesh", "abv": "BD", "selected":false, "isState":false},
        { "name": "Barbados", "abv": "BB", "selected":false, "isState":false},
        { "name": "Belarus", "abv": "BY", "selected":false, "isState":false},
        { "name": "Belgium", "abv": "BE", "selected":false, "isState":false},
        { "name": "Belize", "abv": "BZ", "selected":false, "isState":false},
        { "name": "Benin", "abv": "BJ", "selected":false, "isState":false},
        { "name": "Bermuda", "abv": "BM", "selected":false, "isState":false},
        { "name": "Bhutan", "abv": "BT", "selected":false, "isState":false},
        { "name": "Bolivia", "abv": "BO", "selected":false, "isState":false},
        { "name": "Bosnia and Herzegovina", "abv": "BA", "selected":false, "isState":false},
        { "name": "Botswana", "abv": "BW", "selected":false, "isState":false},
        { "name": "Bouvet Island", "abv": "BV", "selected":false, "isState":false},
        { "name": "Brazil", "abv": "BR", "selected":false, "isState":false},
        { "name": "British Indian Ocean Territory", "abv": "IO", "selected":false, "isState":false},
        { "name": "Brunei Darussalam", "abv": "BN", "selected":false, "isState":false},
        { "name": "Bulgaria", "abv": "BG", "selected":false, "isState":false},
        { "name": "Burkina Faso", "abv": "BF", "selected":false, "isState":false},
        { "name": "Burundi", "abv": "BI", "selected":false, "isState":false},
        { "name": "Cambodia", "abv": "KH", "selected":false, "isState":false},
        { "name": "Cameroon", "abv": "CM", "selected":false, "isState":false},
        { "name": "Canada", "abv": "CA", "selected":false, "isState":false},
        { "name": "Cape Verde", "abv": "CV", "selected":false, "isState":false},
        { "name": "Cayman Islands", "abv": "KY", "selected":false, "isState":false},
        { "name": "Central African Republic", "abv": "CF", "selected":false, "isState":false},
        { "name": "Chad", "abv": "TD", "selected":false, "isState":false},
        { "name": "Chile", "abv": "CL", "selected":false, "isState":false},
        { "name": "China", "abv": "CN", "selected":false, "isState":false},
        { "name": "Christmas Island", "abv": "CX", "selected":false, "isState":false},
        { "name": "Cocos (Keeling) Islands", "abv": "CC", "selected":false, "isState":false},
        { "name": "Colombia", "abv": "CO", "selected":false, "isState":false},
        { "name": "Comoros", "abv": "KM", "selected":false, "isState":false},
        { "name": "Congo", "abv": "CG", "selected":false, "isState":false},
        { "name": "Congo, The Democratic Republic of the", "abv": "CD", "selected":false, "isState":false},
        { "name": "Cook Islands", "abv": "CK", "selected":false, "isState":false},
        { "name": "Costa Rica", "abv": "CR", "selected":false, "isState":false},
        { "name": "Cote D'Ivoire", "abv": "CI", "selected":false, "isState":false},
        { "name": "Croatia", "abv": "HR", "selected":false, "isState":false},
        { "name": "Cuba", "abv": "CU", "selected":false, "isState":false},
        { "name": "Cyprus", "abv": "CY", "selected":false, "isState":false},
        { "name": "Czech Republic", "abv": "CZ", "selected":false, "isState":false},
        { "name": "Denmark", "abv": "DK", "selected":false, "isState":false},
        { "name": "Djibouti", "abv": "DJ", "selected":false, "isState":false},
        { "name": "Dominica", "abv": "DM", "selected":false, "isState":false},
        { "name": "Dominican Republic", "abv": "DO", "selected":false, "isState":false},
        { "name": "Ecuador", "abv": "EC", "selected":false, "isState":false},
        { "name": "Egypt", "abv": "EG", "selected":false, "isState":false},
        { "name": "El Salvador", "abv": "SV", "selected":false, "isState":false},
        { "name": "Equatorial Guinea", "abv": "GQ", "selected":false, "isState":false},
        { "name": "Eritrea", "abv": "ER", "selected":false, "isState":false},
        { "name": "Estonia", "abv": "EE", "selected":false, "isState":false},
        { "name": "Ethiopia", "abv": "ET", "selected":false, "isState":false},
        { "name": "Falkland Islands (Malvinas)", "abv": "FK", "selected":false, "isState":false},
        { "name": "Faroe Islands", "abv": "FO", "selected":false, "isState":false},
        { "name": "Fiji", "abv": "FJ", "selected":false, "isState":false},
        { "name": "Finland", "abv": "FI", "selected":false, "isState":false},
        { "name": "France", "abv": "FR", "selected":false, "isState":false},
        { "name": "French Guiana", "abv": "GF", "selected":false, "isState":false},
        { "name": "French Polynesia", "abv": "PF", "selected":false, "isState":false},
        { "name": "French Southern Territories", "abv": "TF", "selected":false, "isState":false},
        { "name": "Gabon", "abv": "GA", "selected":false, "isState":false},
        { "name": "Gambia", "abv": "GM", "selected":false, "isState":false},
        { "name": "Georgia", "abv": "GE", "selected":false, "isState":false},
        { "name": "Germany", "abv": "DE", "selected":false, "isState":false},
        { "name": "Ghana", "abv": "GH", "selected":false, "isState":false},
        { "name": "Gibraltar", "abv": "GI", "selected":false, "isState":false},
        { "name": "Greece", "abv": "GR", "selected":false, "isState":false},
        { "name": "Greenland", "abv": "GL", "selected":false, "isState":false},
        { "name": "Grenada", "abv": "GD", "selected":false, "isState":false},
        { "name": "Guadeloupe", "abv": "GP", "selected":false, "isState":false},
        { "name": "Guam", "abv": "GU", "selected":false, "isState":false},
        { "name": "Guatemala", "abv": "GT", "selected":false, "isState":false},
        { "name": "Guernsey", "abv": "GG", "selected":false, "isState":false},
        { "name": "Guinea", "abv": "GN", "selected":false, "isState":false},
        { "name": "Guinea-Bissau", "abv": "GW", "selected":false, "isState":false},
        { "name": "Guyana", "abv": "GY", "selected":false, "isState":false},
        { "name": "Haiti", "abv": "HT", "selected":false, "isState":false},
        { "name": "Heard Island and Mcdonald Islands", "abv": "HM", "selected":false, "isState":false},
        { "name": "Holy See (Vatican City State)", "abv": "VA", "selected":false, "isState":false},
        { "name": "Honduras", "abv": "HN", "selected":false, "isState":false},
        { "name": "Hong Kong", "abv": "HK", "selected":false, "isState":false},
        { "name": "Hungary", "abv": "HU", "selected":false, "isState":false},
        { "name": "Iceland", "abv": "IS", "selected":false, "isState":false},
        { "name": "India", "abv": "IN", "selected":false, "isState":false},
        { "name": "Indonesia", "abv": "ID", "selected":false, "isState":false},
        { "name": "Iran, Islamic Republic Of", "abv": "IR", "selected":false, "isState":false},
        { "name": "Iraq", "abv": "IQ", "selected":false, "isState":false},
        { "name": "Ireland", "abv": "IE", "selected":false, "isState":false},
        { "name": "Isle of Man", "abv": "IM", "selected":false, "isState":false},
        { "name": "Israel", "abv": "IL", "selected":false, "isState":false},
        { "name": "Italy", "abv": "IT", "selected":false, "isState":false},
        { "name": "Jamaica", "abv": "JM", "selected":false, "isState":false},
        { "name": "Japan", "abv": "JP", "selected":false, "isState":false},
        { "name": "Jersey", "abv": "JE", "selected":false, "isState":false},
        { "name": "Jordan", "abv": "JO", "selected":false, "isState":false},
        { "name": "Kazakhstan", "abv": "KZ", "selected":false, "isState":false},
        { "name": "Kenya", "abv": "KE", "selected":false, "isState":false},
        { "name": "Kiribati", "abv": "KI", "selected":false, "isState":false},
        { "name": "Korea, Democratic People'S Republic of", "abv": "KP", "selected":false, "isState":false},
        { "name": "Korea, Republic of", "abv": "KR", "selected":false, "isState":false},
        { "name": "Kuwait", "abv": "KW", "selected":false, "isState":false},
        { "name": "Kyrgyzstan", "abv": "KG", "selected":false, "isState":false},
        { "name": "Lao People'S Democratic Republic", "abv": "LA", "selected":false, "isState":false},
        { "name": "Latvia", "abv": "LV", "selected":false, "isState":false},
        { "name": "Lebanon", "abv": "LB", "selected":false, "isState":false},
        { "name": "Lesotho", "abv": "LS", "selected":false, "isState":false},
        { "name": "Liberia", "abv": "LR", "selected":false, "isState":false},
        { "name": "Libyan Arab Jamahiriya", "abv": "LY", "selected":false, "isState":false},
        { "name": "Liechtenstein", "abv": "LI", "selected":false, "isState":false},
        { "name": "Lithuania", "abv": "LT", "selected":false, "isState":false},
        { "name": "Luxembourg", "abv": "LU", "selected":false, "isState":false},
        { "name": "Macao", "abv": "MO", "selected":false, "isState":false},
        { "name": "Macedonia, The Former Yugoslav Republic of", "abv": "MK", "selected":false, "isState":false},
        { "name": "Madagascar", "abv": "MG", "selected":false, "isState":false},
        { "name": "Malawi", "abv": "MW", "selected":false, "isState":false},
        { "name": "Malaysia", "abv": "MY", "selected":false, "isState":false},
        { "name": "Maldives", "abv": "MV", "selected":false, "isState":false},
        { "name": "Mali", "abv": "ML", "selected":false, "isState":false},
        { "name": "Malta", "abv": "MT", "selected":false, "isState":false},
        { "name": "Marshall Islands", "abv": "MH", "selected":false, "isState":false},
        { "name": "Martinique", "abv": "MQ", "selected":false, "isState":false},
        { "name": "Mauritania", "abv": "MR", "selected":false, "isState":false},
        { "name": "Mauritius", "abv": "MU", "selected":false, "isState":false},
        { "name": "Mayotte", "abv": "YT", "selected":false, "isState":false},
        { "name": "Mexico", "abv": "MX", "selected":false, "isState":false},
        { "name": "Micronesia, Federated States of", "abv": "FM", "selected":false, "isState":false},
        { "name": "Moldova, Republic of", "abv": "MD", "selected":false, "isState":false},
        { "name": "Monaco", "abv": "MC", "selected":false, "isState":false},
        { "name": "Mongolia", "abv": "MN", "selected":false, "isState":false},
        { "name": "Montenegro", "abv": "ME", "selected":false, "isState":false},
        { "name": "Montserrat", "abv": "MS", "selected":false, "isState":false},
        { "name": "Morocco", "abv": "MA", "selected":false, "isState":false},
        { "name": "Mozambique", "abv": "MZ", "selected":false, "isState":false},
        { "name": "Myanmar", "abv": "MM", "selected":false, "isState":false},
        { "name": "Namibia", "abv": "NA", "selected":false, "isState":false},
        { "name": "Nauru", "abv": "NR", "selected":false, "isState":false},
        { "name": "Nepal", "abv": "NP", "selected":false, "isState":false},
        { "name": "Netherlands", "abv": "NL", "selected":false, "isState":false},
        { "name": "Netherlands Antilles", "abv": "AN", "selected":false, "isState":false},
        { "name": "New Caledonia", "abv": "NC", "selected":false, "isState":false},
        { "name": "New Zealand", "abv": "NZ", "selected":false, "isState":false},
        { "name": "Nicaragua", "abv": "NI", "selected":false, "isState":false},
        { "name": "Niger", "abv": "NE", "selected":false, "isState":false},
        { "name": "Nigeria", "abv": "NG", "selected":false, "isState":false},
        { "name": "Niue", "abv": "NU", "selected":false, "isState":false},
        { "name": "Norfolk Island", "abv": "NF", "selected":false, "isState":false},
        { "name": "Northern Mariana Islands", "abv": "MP", "selected":false, "isState":false},
        { "name": "Norway", "abv": "NO", "selected":false, "isState":false},
        { "name": "Oman", "abv": "OM", "selected":false, "isState":false},
        { "name": "Pakistan", "abv": "PK", "selected":false, "isState":false},
        { "name": "Palau", "abv": "PW", "selected":false, "isState":false},
        { "name": "Palestinian Territory, Occupied", "abv": "PS", "selected":false, "isState":false},
        { "name": "Panama", "abv": "PA", "selected":false, "isState":false},
        { "name": "Papua New Guinea", "abv": "PG", "selected":false, "isState":false},
        { "name": "Paraguay", "abv": "PY", "selected":false, "isState":false},
        { "name": "Peru", "abv": "PE", "selected":false, "isState":false},
        { "name": "Philippines", "abv": "PH", "selected":false, "isState":false},
        { "name": "Pitcairn", "abv": "PN", "selected":false, "isState":false},
        { "name": "Poland", "abv": "PL", "selected":false, "isState":false},
        { "name": "Portugal", "abv": "PT", "selected":false, "isState":false},
        { "name": "Puerto Rico", "abv": "PR", "selected":false, "isState":false},
        { "name": "Qatar", "abv": "QA", "selected":false, "isState":false},
        { "name": "Reunion", "abv": "RE", "selected":false, "isState":false},
        { "name": "Romania", "abv": "RO", "selected":false, "isState":false},
        { "name": "Russian Federation", "abv": "RU", "selected":false, "isState":false},
        { "name": "RWANDA", "abv": "RW", "selected":false, "isState":false},
        { "name": "Saint Helena", "abv": "SH", "selected":false, "isState":false},
        { "name": "Saint Kitts and Nevis", "abv": "KN", "selected":false, "isState":false},
        { "name": "Saint Lucia", "abv": "LC", "selected":false, "isState":false},
        { "name": "Saint Pierre and Miquelon", "abv": "PM", "selected":false, "isState":false},
        { "name": "Saint Vincent and the Grenadines", "abv": "VC", "selected":false, "isState":false},
        { "name": "Samoa", "abv": "WS", "selected":false, "isState":false},
        { "name": "San Marino", "abv": "SM", "selected":false, "isState":false},
        { "name": "Sao Tome and Principe", "abv": "ST", "selected":false, "isState":false},
        { "name": "Saudi Arabia", "abv": "SA", "selected":false, "isState":false},
        { "name": "Senegal", "abv": "SN", "selected":false, "isState":false},
        { "name": "Serbia", "abv": "RS", "selected":false, "isState":false},
        { "name": "Seychelles", "abv": "SC", "selected":false, "isState":false},
        { "name": "Sierra Leone", "abv": "SL", "selected":false, "isState":false},
        { "name": "Singapore", "abv": "SG", "selected":false, "isState":false},
        { "name": "Slovakia", "abv": "SK", "selected":false, "isState":false},
        { "name": "Slovenia", "abv": "SI", "selected":false, "isState":false},
        { "name": "Solomon Islands", "abv": "SB", "selected":false, "isState":false},
        { "name": "Somalia", "abv": "SO", "selected":false, "isState":false},
        { "name": "South Africa", "abv": "ZA", "selected":false, "isState":false},
        { "name": "South Georgia and the South Sandwich Islands", "abv": "GS", "selected":false, "isState":false},
        { "name": "Spain", "abv": "ES", "selected":false, "isState":false},
        { "name": "Sri Lanka", "abv": "LK", "selected":false, "isState":false},
        { "name": "Sudan", "abv": "SD", "selected":false, "isState":false},
        { "name": "Suriname", "abv": "SR", "selected":false, "isState":false},
        { "name": "Svalbard and Jan Mayen", "abv": "SJ", "selected":false, "isState":false},
        { "name": "Swaziland", "abv": "SZ", "selected":false, "isState":false},
        { "name": "Sweden", "abv": "SE", "selected":false, "isState":false},
        { "name": "Switzerland", "abv": "CH", "selected":false, "isState":false},
        { "name": "Syrian Arab Republic", "abv": "SY", "selected":false, "isState":false},
        { "name": "Taiwan, Province of China", "abv": "TW", "selected":false, "isState":false},
        { "name": "Tajikistan", "abv": "TJ", "selected":false, "isState":false},
        { "name": "Tanzania, United Republic of", "abv": "TZ", "selected":false, "isState":false},
        { "name": "Thailand", "abv": "TH", "selected":false, "isState":false},
        { "name": "Timor-Leste", "abv": "TL", "selected":false, "isState":false},
        { "name": "Togo", "abv": "TG", "selected":false, "isState":false},
        { "name": "Tokelau", "abv": "TK", "selected":false, "isState":false},
        { "name": "Tonga", "abv": "TO", "selected":false, "isState":false},
        { "name": "Trinidad and Tobago", "abv": "TT", "selected":false, "isState":false},
        { "name": "Tunisia", "abv": "TN", "selected":false, "isState":false},
        { "name": "Turkey", "abv": "TR", "selected":false, "isState":false},
        { "name": "Turkmenistan", "abv": "TM", "selected":false, "isState":false},
        { "name": "Turks and Caicos Islands", "abv": "TC", "selected":false, "isState":false},
        { "name": "Tuvalu", "abv": "TV", "selected":false, "isState":false},
        { "name": "Uganda", "abv": "UG", "selected":false, "isState":false},
        { "name": "Ukraine", "abv": "UA", "selected":false, "isState":false},
        { "name": "United Arab Emirates", "abv": "AE", "selected":false, "isState":false},
        { "name": "United Kingdom", "abv": "GB", "selected":false, "isState":false},
        { "name": "United States", "abv": "US", "selected":false, "isState":false},
        { "name": "United States Minor Outlying Islands", "abv": "UM", "selected":false, "isState":false},
        { "name": "Uruguay", "abv": "UY", "selected":false, "isState":false},
        { "name": "Uzbekistan", "abv": "UZ", "selected":false, "isState":false},
        { "name": "Vanuatu", "abv": "VU", "selected":false, "isState":false},
        { "name": "Venezuela", "abv": "VE", "selected":false, "isState":false},
        { "name": "Viet Nam", "abv": "VN", "selected":false, "isState":false},
        { "name": "Virgin Islands, British", "abv": "VG", "selected":false, "isState":false},
        { "name": "Virgin Islands, U.S.", "abv": "VI", "selected":false, "isState":false},
        { "name": "Wallis and Futuna", "abv": "WF", "selected":false, "isState":false},
        { "name": "Western Sahara", "abv": "EH", "selected":false, "isState":false},
        { "name": "Yemen", "abv": "YE", "selected":false, "isState":false},
        { "name": "Zambia", "abv": "ZM", "selected":false, "isState":false},
        { "name": "Zimbabwe", "abv": "ZW", "selected":false, "isState":false }
    ]
    const states = [{ "name": "Alabama", "abv": "AL", "selected":false, "isState":true}, 
     { "name": "Alaska", "abv": "AK", "selected":false, "isState":true}, 
     { "name": "Arizona", "abv": "AZ", "selected":false, "isState":true}, 
     { "name": "Arkansas", "abv": "AR", "selected":false, "isState":true}, 
     { "name": "California", "abv": "CA", "selected":false, "isState":true}, 
     { "name": "Colorado", "abv": "CO", "selected":false, "isState":true}, 
     { "name": "Connecticut", "abv": "CT", "selected":false, "isState":true}, 
     { "name": "Delaware", "abv": "DE", "selected":false, "isState":true}, 
     { "name": "Florida", "abv": "FL", "selected":false, "isState":true}, 
     { "name": "Georgia", "abv": "GA", "selected":false, "isState":true}, 
     { "name": "Hawaii", "abv": "HI", "selected":false, "isState":true}, 
     { "name": "Idaho", "abv": "ID", "selected":false, "isState":true}, 
     { "name": "Illinois", "abv": "IL", "selected":false, "isState":true}, 
     { "name": "Indiana", "abv": "IN", "selected":false, "isState":true}, 
     { "name": "Iowa", "abv": "IA", "selected":false, "isState":true}, 
     { "name": "Kansas", "abv": "KS", "selected":false, "isState":true}, 
     { "name": "Kentucky", "abv": "KY", "selected":false, "isState":true}, 
     { "name": "Louisiana", "abv": "LA", "selected":false, "isState":true}, 
     { "name": "Maine", "abv": "ME", "selected":false, "isState":true}, 
     { "name": "Maryland", "abv": "MD", "selected":false, "isState":true}, 
     { "name": "Massachusetts", "abv": "MA", "selected":false, "isState":true}, 
     { "name": "Michigan", "abv": "MI", "selected":false, "isState":true}, 
     { "name": "Minnesota", "abv": "MN", "selected":false, "isState":true}, 
     { "name": "Mississippi", "abv": "MS", "selected":false, "isState":true}, 
     { "name": "Missouri", "abv": "MO", "selected":false, "isState":true}, 
     { "name": "Montana", "abv": "MT", "selected":false, "isState":true}, 
     { "name": "Nebraska", "abv": "NE", "selected":false, "isState":true}, 
     { "name": "Nevada", "abv": "NV", "selected":false, "isState":true}, 
     { "name": "New Hampshire", "abv": "NH", "selected":false, "isState":true}, 
     { "name": "New Jersey", "abv": "NJ", "selected":false, "isState":true}, 
     { "name": "New Mexico", "abv": "NM", "selected":false, "isState":true}, 
     { "name": "New York", "abv": "NY", "selected":false, "isState":true}, 
     { "name": "North Carolina", "abv": "NC", "selected":false, "isState":true}, 
     { "name": "North Dakota", "abv": "ND", "selected":false, "isState":true}, 
     { "name": "Ohio", "abv": "OH", "selected":false, "isState":true}, 
     { "name": "Oklahoma", "abv": "OK", "selected":false, "isState":true}, 
     { "name": "Oregon", "abv": "OR", "selected":false, "isState":true}, 
     { "name": "Pennsylvania", "abv": "PA", "selected":false, "isState":true}, 
     { "name": "Rhode Island", "abv": "RI", "selected":false, "isState":true}, 
     { "name": "South Carolina", "abv": "SC", "selected":false, "isState":true}, 
     { "name": "South Dakota", "abv": "SD", "selected":false, "isState":true}, 
     { "name": "Tennessee", "abv": "TN", "selected":false, "isState":true}, 
     { "name": "Texas", "abv": "TX", "selected":false, "isState":true}, 
     { "name": "Utah", "abv": "UT", "selected":false, "isState":true}, 
     { "name": "Vermont", "abv": "VT", "selected":false, "isState":true}, 
     { "name": "Virginia", "abv": "VA", "selected":false, "isState":true}, 
     { "name": "Washington", "abv": "WA", "selected":false, "isState":true}, 
     { "name": "West Virginia", "abv": "WV", "selected":false, "isState":true}, 
     { "name": "Wisconsin", "abv": "WI", "selected":false, "isState":true}, 
     { "name": "Wyoming", "abv": "WY", "selected":false, "isState":true }]
     return  countries.concat(states).sort();
} 